/* Copyright (c) 2021-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { AfterViewInit, Directive, ElementRef, Input, NgZone, Optional, Self } from '@angular/core';
import { getScrollContainer } from '../scrolling/scrolling';
import { FocusManager } from '../services/focus-manager';
import { BaseFocusThenScrollDirective, Orientation } from './base-focus-then-scroll.directive';
import { ContinuousNavigationMotionDirective } from './continuous-navigation-motion.directive';

@Directive({
    selector: '[nvSpatialNavigationFocusThenScroll]',
    standalone: true
})
export class SpatialNavigationFocusThenScrollDirective extends BaseFocusThenScrollDirective implements AfterViewInit {
    @Input() scroller?: HTMLElement;

    /**
     * Either `horizontal` or `vertical`
     */
    @Input() get scrollOrientation(): string {
        return this.orientation;
    }
    set scrollOrientation(orientation: string) {
        if (orientation !== 'horizontal' && orientation !== 'vertical') {
            throw new Error('Invalid scroll orientation: ' + orientation);
        }
        this.orientation = orientation;
    }
    protected orientation: Orientation;

    protected get scrollerElement() {
        if (!this._scrollerElement?.isConnected) {
            this._scrollerElement = this.getScrollerElement();
        }
        return this._scrollerElement;
    }
    private _scrollerElement: HTMLElement;

    constructor(
        elementRef: ElementRef<HTMLElement>,
        focusManager: FocusManager,
        ngZone: NgZone,
        @Optional() @Self() continuousNavigationMotion: ContinuousNavigationMotionDirective | null
    ) {
        super(elementRef, focusManager, ngZone, continuousNavigationMotion);
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this._scrollerElement = this.getScrollerElement();
    }

    private getScrollerElement() {
        return this.scroller || getScrollContainer(this.elementRef.nativeElement);
    }

    refreshScrollerElement() {
        this._scrollerElement = this.getScrollerElement();
    }
}
